import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AccessibilityInfo,
  ActivityIndicator,
  Animated,
  Easing,
  Linking,
  Modal,
  PanResponder,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import { t } from "@/localization";
import Button from "./Button";
import FadeOut from "./FadeOut";
import Markdown, { previewBlocks, splitHelpMarkdown } from "./Markdown";
import {
  HELP_SITE,
  helpArticleUrl,
  helpTopic,
  headingAnchor,
} from "./helpTopics";
import { colors, radius, text } from "./theme";

// In-app Help Center. useHelp().openHelp(topicOrId, { view }) opens help for a
// setting (tapping its (?) on phones, tablets and in the apps):
//   - the setting's short explanation, then the start of its Help Center
//     section, fading out under "Read more";
//   - tapping the preview or Read more, or swiping up, expands it to the whole
//     article, scrolled to that section (view "article" opens it expanded).
// Phones get a bottom sheet that follows the finger: swipe up to expand, down
// to collapse or close, or pull the article down from its top. Wide touch
// screens get a centred dialog. Articles load from eventlive.pro/help/<slug>.md
// (public, CORS open); links to other help articles open in the same panel.
// Desktop tooltips (HelpTip.web.js) open the article in a new tab instead.

const HelpContext = createContext(null);
const cache = new Map();
const pending = new Map();

const HELP_LINK =
  /^https?:\/\/(?:www\.)?eventlive\.pro\/help\/([a-z0-9-]+)(?:\.md)?\/?(?:#([\w-]+))?$/i;

const NATIVE_DRIVER = Platform.OS !== "web";
// Close to the system sheets: settles in about 0.4 s without bouncing.
const SPRING = {
  stiffness: 260,
  damping: 30,
  mass: 1,
  restDisplacementThreshold: 0.5,
  restSpeedThreshold: 0.5,
};
const PREVIEW_HEIGHT = 168;
// White below the sheet, so a bounce past the top never shows the backdrop.
const OVERSCROLL = 160;
const BACKDROP = "rgba(17,24,39,0.45)";

const openExternal = (url) => {
  if (Platform.OS === "web") window.open(url, "_blank", "noopener");
  else Linking.openURL(url).catch(() => {});
};

function loadArticle(slug) {
  if (cache.has(slug)) return Promise.resolve(cache.get(slug));
  if (pending.has(slug)) return pending.get(slug);
  const request = fetch(`${HELP_SITE}/help/${slug}.md`)
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    })
    .then((md) => {
      const article = splitHelpMarkdown(md);
      cache.set(slug, article);
      return article;
    })
    .finally(() => pending.delete(slug));
  pending.set(slug, request);
  return request;
}

function useHelpArticle(slug) {
  const [state, setState] = useState(() =>
    cache.has(slug) ? { article: cache.get(slug) } : { loading: true }
  );
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    if (cache.has(slug)) {
      setState({ article: cache.get(slug) });
    } else {
      setState({ loading: true });
      loadArticle(slug)
        .then((article) => active && setState({ article }))
        .catch((error) => active && setState({ error }));
    }
    return () => {
      active = false;
    };
  }, [slug, attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

function useReduceMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    let active = true;
    AccessibilityInfo.isReduceMotionEnabled?.()
      .then((value) => active && setReduce(!!value))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  return reduce;
}

// A touch's position: React Native puts it on the event, the web on the touch.
const pageYOf = (e) => {
  const { nativeEvent: n } = e;
  return n.pageY ?? (n.touches?.[0] || n.changedTouches?.[0])?.pageY ?? 0;
};

const toTopic = (topicOrId) =>
  typeof topicOrId === "string" ? helpTopic(topicOrId) : topicOrId || null;

// Links in an article: other help articles open in the panel, the rest in
// the browser.
const helpLinkHandler = (onNavigate) => (href) => {
  const url = href.startsWith("/") ? `${HELP_SITE}${href}` : href;
  const match = url.match(HELP_LINK);
  if (match) {
    onNavigate({
      slug: match[1],
      section: match[2] ? match[2].replace(/-/g, " ") : null,
    });
  } else {
    openExternal(url);
  }
};

const Loading = () => (
  <View style={tw`py-16 items-center`}>
    <ActivityIndicator
      color={colors.muted}
      accessibilityLabel={t("Loading the article")}
    />
  </View>
);

const ArticleError = ({ onRetry }) => (
  <View style={tw`py-2`}>
    <Text style={[text.hint, tw`mb-3`]}>
      {t("The article didn't load. Check your connection and try again.")}
    </Text>
    <Button
      text={t("Try again")}
      variant="secondary"
      size="sm"
      icon="refresh"
      fullWidth={false}
      onPress={onRetry}
    />
  </View>
);

// The whole article, scrolled to `section` (highlighted) once it's laid out.
function ArticleBody({
  article,
  section,
  onLinkPress,
  scrollProps,
  bottomInset,
}) {
  const scrollRef = useRef(null);
  const bodyY = useRef(0);
  const headings = useRef({});
  const scrolled = useRef(false);
  const target = section ? headingAnchor(section) : null;

  const scrollToTarget = () => {
    if (!target || scrolled.current) return;
    const y = headings.current[target];
    if (y === undefined) return;
    scrolled.current = true;
    scrollRef.current?.scrollTo({
      y: Math.max(0, bodyY.current + y - 12),
      animated: false,
    });
  };

  return (
    <ScrollView
      ref={scrollRef}
      style={[tw`flex-1`, Platform.OS === "web" && styles.containScroll]}
      contentContainerStyle={{ paddingBottom: 24 + (bottomInset || 0) }}
      scrollEventThrottle={16}
      {...scrollProps}
    >
      <Text style={[text.modalTitle, tw`mb-1`]}>{article.title}</Text>
      {!!article.description && (
        <Text style={[text.hint, tw`text-[14px] leading-[20px] mb-4`]}>
          {article.description}
        </Text>
      )}
      <View
        onLayout={(e) => {
          bodyY.current = e.nativeEvent.layout.y;
          scrollToTarget();
        }}
      >
        <Markdown
          markdown={article.body}
          highlight={section}
          onLinkPress={onLinkPress}
          onHeadingLayout={(anchor, y) => {
            headings.current[anchor] = y;
            if (anchor === target) setTimeout(scrollToTarget, 0);
          }}
        />
      </View>
    </ScrollView>
  );
}

function ArticleView({
  slug,
  section,
  onNavigate,
  onTitle,
  scrollProps,
  bottomInset,
}) {
  const { loading, article, error, retry } = useHelpArticle(slug);
  useEffect(() => {
    if (article) onTitle?.(article.title);
  }, [article]);
  if (loading) return <Loading />;
  if (error) return <ArticleError onRetry={retry} />;
  return (
    <ArticleBody
      article={article}
      section={section}
      onLinkPress={helpLinkHandler(onNavigate)}
      scrollProps={scrollProps}
      bottomInset={bottomInset}
    />
  );
}

const ReadMore = ({ onPress }) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={t("Read the full article")}
    hitSlop={8}
    style={({ pressed, hovered }) => [
      styles.readMore,
      (pressed || hovered) && { backgroundColor: colors.surfaceStrong },
    ]}
  >
    <Text style={[text.bodyStrong, { color: colors.slate }]}>
      {t("Read more")}
    </Text>
    <Ionicons
      name="chevron-up"
      size={16}
      color={colors.slate}
      style={tw`ml-1.5`}
    />
  </Pressable>
);

// The start of the article's section, blurring and fading out under
// "Read more". Tapping anywhere on it expands.
function ArticlePreview({ article, section, onExpand }) {
  const blocks = useMemo(
    () => previewBlocks(article.body, section),
    [article, section]
  );
  const [contentH, setContentH] = useState(0);
  const clipped = !contentH || contentH > PREVIEW_HEIGHT;
  return (
    <Pressable
      onPress={onExpand}
      accessibilityRole="button"
      accessibilityLabel={t('Read more in "{title}"', { title: article.title })}
      style={styles.previewCard}
    >
      <View style={tw`flex-row items-center mb-1.5`}>
        <Ionicons
          name="book-outline"
          size={14}
          color={colors.muted}
          style={tw`mr-1.5`}
        />
        <Text style={[text.hint, tw`flex-1`]} numberOfLines={1}>
          {article.title}
        </Text>
      </View>
      <View
        style={{
          height: contentH
            ? Math.min(contentH, PREVIEW_HEIGHT)
            : PREVIEW_HEIGHT,
          overflow: "hidden",
        }}
      >
        <View
          onLayout={(e) => setContentH(Math.ceil(e.nativeEvent.layout.height))}
        >
          <Markdown blocks={blocks} compact onLinkPress={onExpand} />
        </View>
        <FadeOut height={clipped ? 96 : 40} color={colors.surface} />
      </View>
      <View style={tw`items-center mt-1`}>
        <ReadMore onPress={onExpand} />
      </View>
    </Pressable>
  );
}

const PreviewSkeleton = () => (
  <View
    style={[styles.previewCard, { height: 196 }]}
    accessibilityLabel={t("Loading the article")}
  >
    {[0.5, 0.35, 1, 0.94, 0.86, 0.97].map((width, i) => (
      <View
        key={i}
        style={[
          styles.skeletonLine,
          { width: `${width * 100}%` },
          i === 0 && { height: 10, marginBottom: 14 },
          i === 1 && { height: 14 },
        ]}
      />
    ))}
    <FadeOut height={72} color={colors.surface} />
  </View>
);

// What a help tip opens with: its explanation and the article preview.
function PeekContent({ topic, preview, onExpand }) {
  return (
    <View>
      {!!topic.summary && (
        <Text style={[text.body, tw`text-[15px] leading-[22px] mb-4`]}>
          {t(topic.summary)}
        </Text>
      )}
      {preview.article ? (
        <ArticlePreview
          article={preview.article}
          section={topic.section}
          onExpand={onExpand}
        />
      ) : preview.error ? (
        <ArticleError onRetry={preview.retry} />
      ) : (
        <PreviewSkeleton />
      )}
    </View>
  );
}

const PanelHeader = ({ icon, iconLabel, onIconPress, title, onClose }) => (
  <View style={tw`flex-row items-center`}>
    {onIconPress ? (
      <Pressable
        onPress={onIconPress}
        accessibilityRole="button"
        accessibilityLabel={iconLabel}
        hitSlop={10}
        style={tw`mr-2 p-1 -ml-1`}
      >
        <Ionicons name={icon} size={22} color={colors.slate} />
      </Pressable>
    ) : (
      <Ionicons name={icon} size={22} color={colors.slate} style={tw`mr-2`} />
    )}
    <Text style={[text.label, tw`flex-1 text-[15px]`]} numberOfLines={1}>
      {title}
    </Text>
    <Pressable
      onPress={onClose}
      accessibilityRole="button"
      accessibilityLabel={t("Close help")}
      hitSlop={10}
      style={({ hovered }) => [
        tw`p-1 rounded-full`,
        hovered && { backgroundColor: colors.surfaceStrong },
      ]}
    >
      <Ionicons name="close" size={22} color={colors.muted} />
    </Pressable>
  </View>
);

// Phones and the apps: a bottom sheet. It opens at the height of the preview
// ("peek") and expands to almost the full screen. The sheet is always laid out
// at full height and slid down, so moving it is a transform the native driver
// animates while the article renders.
function HelpSheet({ request, onClose }) {
  const topic = request.topic;
  const preview = useHelpArticle(topic.slug);
  const { height: winH } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const reduceMotion = useReduceMotion();

  const fullH = Math.round(Math.min(winH - insets.top - 8, winH * 0.94));
  const bottomPad = Math.max(insets.bottom, 16);
  const [headerH, setHeaderH] = useState(0);
  const [peekContentH, setPeekContentH] = useState(0);
  const measured = headerH > 0 && peekContentH > 0;
  const peekH = Math.min(
    headerH + peekContentH + bottomPad + 8,
    Math.round(winH * 0.72)
  );
  const peekY = Math.max(1, fullH - peekH);

  const y = useRef(new Animated.Value(winH)).current;
  const rest = useRef(winH);
  const dragStart = useRef(0);
  const dragging = useRef(false);
  const opened = useRef(false);
  const closing = useRef(false);
  const [expanded, setExpandedState] = useState(request.view === "article");
  const expandedRef = useRef(expanded);
  const setExpanded = (value) => {
    expandedRef.current = value;
    setExpandedState(value);
  };
  const [history, setHistory] = useState([]);
  const [articleTitle, setArticleTitle] = useState("");
  const scrollY = useRef(0);
  const touch = useRef(null);

  // The gesture callbacks are created once; they read the latest layout here.
  const layout = useRef({});
  layout.current = { peekY, fullH };

  const animateTo = useCallback(
    (to, velocity = 0, done) => {
      rest.current = to;
      const animation = reduceMotion
        ? Animated.timing(y, {
            toValue: to,
            duration: 180,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: NATIVE_DRIVER,
          })
        : Animated.spring(y, {
            toValue: to,
            velocity,
            ...SPRING,
            overshootClamping: !!done,
            useNativeDriver: NATIVE_DRIVER,
          });
      animation.start(({ finished }) => finished && done?.());
    },
    [y, reduceMotion]
  );

  const closed = useRef(false);
  const closeTimer = useRef(null);
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  const finishClose = useCallback(() => {
    if (closed.current) return;
    closed.current = true;
    onClose();
  }, [onClose]);
  const close = useCallback(
    (velocity = 0) => {
      if (closing.current) return;
      closing.current = true;
      animateTo(layout.current.fullH + 24, Math.max(velocity, 0), finishClose);
      // If the animation is interrupted, still never leave an invisible modal.
      closeTimer.current = setTimeout(finishClose, 700);
    },
    [animateTo, finishClose]
  );

  const expand = useCallback(
    (velocity = 0) => {
      if (closing.current) return;
      setExpanded(true);
      animateTo(0, velocity);
    },
    [animateTo]
  );

  const collapse = useCallback(
    (velocity = 0) => {
      if (closing.current || !expandedRef.current) return;
      setExpanded(false);
      setHistory([]);
      animateTo(layout.current.peekY, velocity);
    },
    [animateTo]
  );

  // Where a drag ends, from where the sheet is heading: its position plus
  // 0.4 s at the release speed (iOS projects a flick about that far).
  // - From half height, a short swipe down closes it; a swipe up expands it.
  // - From full height, a swipe down goes back to half height. It closes
  //   only when the sheet itself was dragged well below half height.
  const settle = useCallback(
    (position, vy) => {
      dragging.current = false;
      const { peekY: peek, fullH: full } = layout.current;
      const heading = position + vy * 400;
      const velocity = vy * 1000;
      const fromExpanded = dragStart.current < peek / 2;
      if (fromExpanded) {
        if (position > peek + (full - peek) * 0.3) close(velocity);
        else if (heading < peek / 2) expand(velocity);
        else collapse(velocity);
      } else if (heading > peek + (full - peek) * 0.25) close(velocity);
      else if (heading < peek / 2) expand(velocity);
      else animateTo(peek, velocity);
    },
    [animateTo, close, collapse, expand]
  );

  const pan = useMemo(
    () =>
      PanResponder.create({
        // Claim the touch on start: on iOS and Android the Modal itself
        // claims every touch nothing inside it claims, and a responder that
        // only asks on move would never see the drag. Buttons and links
        // inside are deeper, so they still get their taps first.
        onStartShouldSetPanResponder: () => !closing.current,
        onMoveShouldSetPanResponder: (_, g) =>
          !closing.current &&
          Math.abs(g.dy) > 6 &&
          Math.abs(g.dy) > Math.abs(g.dx) * 1.2,
        onPanResponderGrant: () => {
          y.stopAnimation();
          dragging.current = true;
          dragStart.current = rest.current;
        },
        onPanResponderMove: (_, g) => {
          const next = dragStart.current + g.dy;
          // Past the top it resists, like the system sheets.
          y.setValue(next < 0 ? -Math.sqrt(-next) * 3 : next);
        },
        onPanResponderRelease: (_, g) => settle(dragStart.current + g.dy, g.vy),
        onPanResponderTerminate: (_, g) =>
          settle(dragStart.current + g.dy, g.vy),
        onPanResponderTerminationRequest: () => false,
      }),
    [y, settle]
  );

  // Open once the peek height is known; follow it while the preview loads.
  useEffect(() => {
    if (!measured || closing.current || dragging.current) return;
    if (!opened.current) {
      opened.current = true;
      animateTo(expandedRef.current ? 0 : peekY);
    } else if (!expandedRef.current) {
      animateTo(peekY);
    }
  }, [measured, peekY]);

  // Pulling the article down from its top collapses the sheet: iOS through
  // the bounce, Android through the scroll view's drag events, the web
  // through touch events.
  const articleTouch = {
    onTouchStart: (e) => {
      touch.current = {
        y: pageYOf(e),
        atTop: scrollY.current <= 0,
        dy: 0,
      };
    },
    onTouchMove: (e) => {
      if (touch.current) touch.current.dy = pageYOf(e) - touch.current.y;
    },
    onTouchEnd: () => {
      const gesture = touch.current;
      touch.current = null;
      if (gesture?.atTop && gesture.dy > 80 && scrollY.current <= 0) collapse();
    },
  };
  const scrollProps = {
    onScroll: (e) => {
      scrollY.current = e.nativeEvent.contentOffset.y;
    },
    onScrollEndDrag: (e) => {
      const offset = e.nativeEvent.contentOffset.y;
      const gesture = touch.current;
      if (
        Platform.OS === "ios"
          ? offset < -48
          : gesture?.atTop && gesture.dy > 6 && offset <= 0
      ) {
        collapse();
      }
    },
  };

  const { backdropOpacity, previewOpacity, articleOpacity } = useMemo(
    () => ({
      backdropOpacity: y.interpolate({
        inputRange: [peekY, fullH],
        outputRange: [1, 0],
        extrapolate: "clamp",
      }),
      previewOpacity: y.interpolate({
        inputRange: [peekY * 0.5, peekY],
        outputRange: [0, 1],
        extrapolate: "clamp",
      }),
      articleOpacity: y.interpolate({
        inputRange: [0, peekY * 0.6],
        outputRange: [1, 0],
        extrapolate: "clamp",
      }),
    }),
    [y, peekY, fullH]
  );

  const current = history.length
    ? history[history.length - 1]
    : { slug: topic.slug, section: topic.section };

  return (
    <Modal
      transparent
      visible
      animationType="none"
      statusBarTranslucent
      onRequestClose={() => close()}
    >
      <Animated.View
        style={[
          StyleSheet.absoluteFill,
          { backgroundColor: BACKDROP, opacity: backdropOpacity },
        ]}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={() => close()}
          accessibilityRole="button"
          accessibilityLabel={t("Close help")}
        />
      </Animated.View>
      <View style={styles.sheetFrame} pointerEvents="box-none">
        <Animated.View
          accessibilityViewIsModal
          style={[
            styles.sheet,
            {
              height: fullH + OVERSCROLL,
              marginBottom: -OVERSCROLL,
              transform: [{ translateY: y }],
            },
          ]}
        >
          <View style={{ height: fullH }}>
            <View
              {...pan.panHandlers}
              onLayout={(e) =>
                setHeaderH(Math.round(e.nativeEvent.layout.height))
              }
              style={[
                tw`px-5 pt-2 pb-3`,
                Platform.OS === "web" && styles.webDrag,
              ]}
            >
              <View style={styles.grabber} />
              <PanelHeader
                icon={
                  !expanded
                    ? "help-circle"
                    : history.length
                    ? "chevron-back"
                    : "chevron-down"
                }
                iconLabel={history.length ? t("Back") : t("Show less")}
                onIconPress={
                  !expanded
                    ? null
                    : history.length
                    ? () => setHistory((h) => h.slice(0, -1))
                    : () => collapse()
                }
                title={
                  expanded ? articleTitle || t(topic.title) : t(topic.title)
                }
                onClose={() => close()}
              />
            </View>
            <View style={tw`flex-1`}>
              <Animated.View
                {...articleTouch}
                pointerEvents={expanded ? "auto" : "none"}
                accessibilityElementsHidden={!expanded}
                importantForAccessibility={
                  expanded ? "auto" : "no-hide-descendants"
                }
                style={[
                  StyleSheet.absoluteFill,
                  tw`px-5`,
                  { opacity: articleOpacity },
                ]}
              >
                <ArticleView
                  key={`${current.slug}#${current.section || ""}`}
                  slug={current.slug}
                  section={current.section}
                  onTitle={setArticleTitle}
                  onNavigate={(next) => setHistory((h) => [...h, next])}
                  scrollProps={scrollProps}
                  bottomInset={bottomPad}
                />
              </Animated.View>
              <Animated.View
                {...pan.panHandlers}
                pointerEvents={expanded ? "none" : "auto"}
                accessibilityElementsHidden={expanded}
                importantForAccessibility={
                  expanded ? "no-hide-descendants" : "auto"
                }
                style={[
                  StyleSheet.absoluteFill,
                  tw`px-5`,
                  { opacity: previewOpacity },
                ]}
              >
                <View
                  onLayout={(e) =>
                    setPeekContentH(Math.round(e.nativeEvent.layout.height))
                  }
                >
                  <PeekContent
                    topic={topic}
                    preview={preview}
                    onExpand={() => expand()}
                  />
                </View>
              </Animated.View>
            </View>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

// Wide touch screens (tablets in a browser): a centred dialog with the same
// preview; Read more swaps it for the article.
function HelpDialog({ request, onClose }) {
  const topic = request.topic;
  const preview = useHelpArticle(topic.slug);
  const [expanded, setExpanded] = useState(request.view === "article");
  const [history, setHistory] = useState([]);
  const [articleTitle, setArticleTitle] = useState("");
  const current = history.length
    ? history[history.length - 1]
    : { slug: topic.slug, section: topic.section };

  return (
    <Modal transparent animationType="fade" visible onRequestClose={onClose}>
      <View style={tw`flex-1 items-center justify-center p-6`}>
        <Pressable
          style={[StyleSheet.absoluteFill, { backgroundColor: BACKDROP }]}
          onPress={onClose}
          accessibilityLabel={t("Close help")}
        />
        <View
          style={[
            styles.dialog,
            { maxWidth: expanded ? 680 : 480, maxHeight: "85%" },
            expanded && { height: 720 },
          ]}
        >
          <View style={tw`mb-3`}>
            <PanelHeader
              icon={expanded ? "chevron-back" : "help-circle"}
              iconLabel={t("Back")}
              onIconPress={
                !expanded
                  ? null
                  : () =>
                      history.length
                        ? setHistory((h) => h.slice(0, -1))
                        : setExpanded(false)
              }
              title={expanded ? articleTitle || t(topic.title) : t(topic.title)}
              onClose={onClose}
            />
          </View>
          {expanded ? (
            <View style={tw`flex-1`}>
              <ArticleView
                key={`${current.slug}#${current.section || ""}`}
                slug={current.slug}
                section={current.section}
                onTitle={setArticleTitle}
                onNavigate={(next) => setHistory((h) => [...h, next])}
              />
            </View>
          ) : (
            <PeekContent
              topic={topic}
              preview={preview}
              onExpand={() => setExpanded(true)}
            />
          )}
        </View>
      </View>
    </Modal>
  );
}

function HelpPanel(props) {
  const isMobileUX = useIsMobileUX();
  return Platform.OS !== "web" || isMobileUX ? (
    <HelpSheet {...props} />
  ) : (
    <HelpDialog {...props} />
  );
}

export function HelpProvider({ children }) {
  const [request, setRequest] = useState(null);
  const openHelp = useCallback((topicOrId, { view = "summary" } = {}) => {
    const topic = toTopic(topicOrId);
    if (!topic?.slug) return;
    setRequest({ topic, view, key: Date.now() });
  }, []);
  const closeHelp = useCallback(() => setRequest(null), []);
  const value = useMemo(() => ({ openHelp, closeHelp }), [openHelp, closeHelp]);
  return (
    <HelpContext.Provider value={value}>
      {children}
      {!!request && (
        <HelpPanel key={request.key} request={request} onClose={closeHelp} />
      )}
    </HelpContext.Provider>
  );
}

// Outside a provider (tests, isolated screens) help opens in the browser.
export const useHelp = () =>
  useContext(HelpContext) || {
    openHelp: (topicOrId) => {
      const topic = toTopic(topicOrId);
      if (topic?.slug) openExternal(helpArticleUrl(topic.slug, topic.section));
    },
    closeHelp: () => {},
  };

const styles = StyleSheet.create({
  // Spelled out: React Native 0.86 has no StyleSheet.absoluteFillObject
  // (only the web does), and without it the sheet sat at the top.
  sheetFrame: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "flex-end",
  },
  sheet: {
    width: "100%",
    maxWidth: 640,
    backgroundColor: colors.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    shadowColor: "#000",
    shadowOpacity: 0.16,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: -2 },
    elevation: 16,
  },
  grabber: {
    alignSelf: "center",
    width: 36,
    height: 5,
    borderRadius: 3,
    backgroundColor: colors.border,
    marginBottom: 10,
  },
  previewCard: {
    backgroundColor: colors.surface,
    borderRadius: radius.card,
    borderWidth: 1,
    borderColor: colors.divider,
    paddingHorizontal: 14,
    paddingTop: 12,
    paddingBottom: 12,
    overflow: "hidden",
  },
  readMore: {
    flexDirection: "row",
    alignItems: "center",
    height: 36,
    paddingHorizontal: 16,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.white,
  },
  skeletonLine: {
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.surfaceStrong,
    marginBottom: 12,
  },
  dialog: {
    width: "100%",
    backgroundColor: colors.white,
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 20,
    shadowColor: "#000",
    shadowOpacity: 0.2,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
  },
  webDrag: { touchAction: "none", cursor: "grab" },
  containScroll: { overscrollBehavior: "contain" },
});
